import React from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import useTranslation from "../../hooks/useTranslation";

export const reportsOptions = ["Weekly", "Monthly", "Yearly", "Burnout"];

export default function ReportsPanel({ reportFilter, setReportFilter, sessions, appointments, patients }) {
  const { t } = useTranslation();

  const calculateReportData = () => {
    const now = new Date();
    let periodData = [];
    let chartData = [];

    if (reportFilter === "Weekly") {
      for (let i = 3; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - (i * 7));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const weekSessions = sessions.filter(s => {
          if (!s.session_date) return false;
          const sessionDate = new Date(s.session_date);
          return sessionDate >= weekStart && sessionDate <= weekEnd;
        });

        const weekAppointments = appointments.filter(a => {
          if (!a.date) return false;
          const apptDate = new Date(a.date);
          return apptDate >= weekStart && apptDate <= weekEnd;
        });

        const avgBurnout = weekSessions.length > 0
          ? weekSessions.reduce((sum, s) => sum + (s.emotions?.burnout_score || 0), 0) / weekSessions.length
          : 0;

        const completedAppointments = weekAppointments.filter(a => {
          const apptDateTime = new Date(a.date + ' ' + (a.time || '00:00'));
          return weekSessions.some(s => {
            const sessionDate = new Date(s.session_date);
            const timeDiff = Math.abs(sessionDate - apptDateTime);
            return timeDiff <= 90 * 60 * 1000;
          });
        }).length;

        periodData.push({
          period: `W${4 - i}`,
          sessions: weekSessions.length,
          appointments: weekAppointments.length,
          completed: completedAppointments,
          avgBurnout: Math.round(avgBurnout),
          completionRate: weekAppointments.length > 0
            ? Math.round((completedAppointments / weekAppointments.length) * 100)
            : 0
        });
      }
      chartData = periodData.map(d => ({
        name: d.period,
        sessions: d.sessions,
        completion: d.completionRate,
        burnout: d.avgBurnout
      }));
    } else if (reportFilter === "Monthly") {
      for (let i = 3; i >= 0; i--) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);

        const monthSessions = sessions.filter(s => {
          if (!s.session_date) return false;
          const sessionDate = new Date(s.session_date);
          return sessionDate >= monthStart && sessionDate <= monthEnd;
        });

        const monthAppointments = appointments.filter(a => {
          if (!a.date) return false;
          const apptDate = new Date(a.date);
          return apptDate >= monthStart && apptDate <= monthEnd;
        });

        const avgBurnout = monthSessions.length > 0
          ? monthSessions.reduce((sum, s) => sum + (s.emotions?.burnout_score || 0), 0) / monthSessions.length
          : 0;

        const completedAppointments = monthAppointments.filter(a => {
          const apptDateTime = new Date(a.date + ' ' + (a.time || '00:00'));
          return monthSessions.some(s => {
            const sessionDate = new Date(s.session_date);
            const timeDiff = Math.abs(sessionDate - apptDateTime);
            return timeDiff <= 90 * 60 * 1000;
          });
        }).length;

        const monthName = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        periodData.push({
          period: monthName,
          sessions: monthSessions.length,
          appointments: monthAppointments.length,
          completed: completedAppointments,
          avgBurnout: Math.round(avgBurnout),
          completionRate: monthAppointments.length > 0
            ? Math.round((completedAppointments / monthAppointments.length) * 100)
            : 0
        });
      }
      chartData = periodData.map(d => ({
        name: d.period,
        sessions: d.sessions,
        completion: d.completionRate,
        burnout: d.avgBurnout
      }));
    } else if (reportFilter === "Yearly") {
      for (let i = 3; i >= 0; i--) {
        const quarter = Math.floor((now.getMonth() - (i * 3)) / 3);
        const year = now.getFullYear() - Math.floor((now.getMonth() - (i * 3)) / 12);
        const quarterStart = new Date(year, quarter * 3, 1);
        const quarterEnd = new Date(year, (quarter + 1) * 3, 0, 23, 59, 59, 999);

        const quarterSessions = sessions.filter(s => {
          if (!s.session_date) return false;
          const sessionDate = new Date(s.session_date);
          return sessionDate >= quarterStart && sessionDate <= quarterEnd;
        });

        const quarterAppointments = appointments.filter(a => {
          if (!a.date) return false;
          const apptDate = new Date(a.date);
          return apptDate >= quarterStart && apptDate <= quarterEnd;
        });

        const avgBurnout = quarterSessions.length > 0
          ? quarterSessions.reduce((sum, s) => sum + (s.emotions?.burnout_score || 0), 0) / quarterSessions.length
          : 0;

        periodData.push({
          period: `Q${quarter + 1} ${year}`,
          sessions: quarterSessions.length,
          appointments: quarterAppointments.length,
          avgBurnout: Math.round(avgBurnout)
        });
      }
      chartData = periodData.map(d => ({
        name: d.period,
        sessions: d.sessions,
        burnout: d.avgBurnout
      }));
    } else if (reportFilter === "Burnout") {
      for (let i = 7; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - (i * 7));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const weekSessions = sessions.filter(s => {
          if (!s.session_date) return false;
          const sessionDate = new Date(s.session_date);
          return sessionDate >= weekStart && sessionDate <= weekEnd;
        });

        const avgBurnout = weekSessions.length > 0
          ? weekSessions.reduce((sum, s) => sum + (s.emotions?.burnout_score || 0), 0) / weekSessions.length
          : 0;

        const highRiskCount = patients.filter(p => p.status === "High Risk").length;
        const moderateRiskCount = patients.filter(p => p.status === "Moderate Risk").length;

        periodData.push({
          period: `W${8 - i}`,
          avgBurnout: Math.round(avgBurnout),
          highRisk: highRiskCount,
          moderateRisk: moderateRiskCount
        });
      }
      chartData = periodData.map(d => ({
        name: d.period,
        burnout: d.avgBurnout,
        highRisk: d.highRisk,
        moderateRisk: d.moderateRisk
      }));
    }

    return { periodData, chartData };
  };

  const { chartData } = calculateReportData();

  return (
    <div className="admin-panel">
      <div className="admin-panel-title">{t('Reports')}</div>
      <div className="admin-row">
        <select
          value={reportFilter}
          onChange={(e) => setReportFilter(e.target.value)}
          className="admin-select"
        >
          {reportsOptions.map((o) => (
            <option key={o} value={o}>
              {t(o)} {t('Report')}
            </option>
          ))}
        </select>
      </div>
      <div className="admin-chart">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            {reportFilter === "Burnout" ? (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="burnout" stroke="#ef4444" strokeWidth={2} name="Avg Burnout" />
                <Line type="monotone" dataKey="highRisk" stroke="#dc2626" strokeWidth={2} name="High Risk Patients" />
                <Line type="monotone" dataKey="moderateRisk" stroke="#f59e0b" strokeWidth={2} name="Moderate Risk" />
              </LineChart>
            ) : (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sessions" stroke="#3b82f6" strokeWidth={2} name="Sessions" />
                {reportFilter !== "Yearly" && (
                  <Line type="monotone" dataKey="completion" stroke="#10b981" strokeWidth={2} name="Completion %" />
                )}
                <Line type="monotone" dataKey="burnout" stroke="#ef4444" strokeWidth={2} name="Avg Burnout" />
              </LineChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            {t('No data available for')} {t(reportFilter)} {t('report')}
          </div>
        )}
      </div>
    </div>
  );
}
